import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";

import { createFilter } from "react-select";
import Tooltipable from "~/components/effects/tooltipable";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import { NOOP } from "~/constants/callbacks";
import brokerbit from "~/lib/brokerbit";

const defaultFilter = createFilter({ ignoreAccents: false });
const templateFilterOption = (option, inputValue) => {
  if (option.value === -1) {
    return true;
  }
  return defaultFilter(option, inputValue ?? "");
};

const buildTemplateLabel = (template, currentUserId) => {
  if (!template.shared) {
    return template.title;
  }
  if (template.user_id === currentUserId) {
    return template.title;
  }

  const firstName = template.owner_first_name;
  if (!firstName) {
    return template.title;
  }

  const lastName = template.owner_last_name
    ? ` ${template.owner_last_name}`
    : "";
  return `${template.title} (Shared - ${firstName.charAt(0)}.${lastName})`;
};

const TemplateSelect = ({
  templates = [],
  selectedTemplate = null,
  setSelectedTemplate,
  templateChanged = false,
  setTemplateChanged,
  isLoading = false,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
  isCopying = false,
  isSharing = false,
  updateTemplate = null,
  deleteTemplate = NOOP,
  copyTemplate = NOOP,
  shareTemplate = NOOP,
  unshareTemplate = NOOP,
  trackApply = null,
  applyTemplate = NOOP,
  onCreate = NOOP,
  isAdmin = false,
  currentUserId = null,
  infoTooltip = null,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedTemplateObject = selectedTemplate
    ? templates.find((t) => t.id === selectedTemplate.value)
    : null;

  const isOwnTemplate =
    selectedTemplateObject && selectedTemplateObject.user_id === currentUserId;
  const isSharedTemplate =
    selectedTemplateObject && selectedTemplateObject.shared;

  const handleTemplateSelect = (selectedOption) => {
    if (!selectedOption) {
      // Clear template selection without clearing form fields
      setSelectedTemplate(null);
      setTemplateChanged(false);
      return;
    }

    if (selectedOption.value === -1) {
      onCreate();
    } else {
      setSelectedTemplate(selectedOption);
      setTemplateChanged(false);
      const template = templates.find((t) => t.id === selectedOption.value);
      if (trackApply) {
        trackApply(template);
      }
      applyTemplate(template);
    }
  };

  const handleTemplateDelete = (e) => {
    e.preventDefault();
    brokerbit.confirmBox({
      message:
        "Are you sure you want to delete this template? This cannot be undone.",
      callback: (ok) => {
        if (ok) {
          deleteTemplate(selectedTemplate.value);
        }
      },
    });
  };

  const handleUpdateTemplate = (e) => {
    e.preventDefault();
    if (updateTemplate) {
      updateTemplate();
    }
  };

  const handleCopyTemplate = (e) => {
    e.preventDefault();
    copyTemplate(selectedTemplate.value);
  };

  const handleShareTemplate = (e) => {
    e.preventDefault();
    shareTemplate(selectedTemplate.value);
  };

  const handleUnshareTemplate = (e) => {
    e.preventDefault();
    unshareTemplate(selectedTemplate.value);
  };

  const buildTemplateOptions = () => {
    const options = [{ label: "- Create New Template -", value: -1 }];
    if (templates) {
      templates.forEach((template) => {
        options.push({
          label: buildTemplateLabel(template, currentUserId),
          value: template.id,
        });
      });
    }
    return options;
  };

  const isBusy =
    isLoading ||
    isCreating ||
    isUpdating ||
    isDeleting ||
    isCopying ||
    isSharing;

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const renderDropdownItems = () => {
    if (!selectedTemplate) {
      return null;
    }

    const items = [];

    // Save changes — only own templates with update support
    if (isOwnTemplate && updateTemplate) {
      items.push(
        <Dropdown.Item
          key="save"
          disabled={!templateChanged || isUpdating}
          onClick={handleUpdateTemplate}
        >
          {isUpdating ? "Saving..." : "Save changes"}
        </Dropdown.Item>,
      );
    }

    // Copy to my templates — only for templates you don't own
    if (!isOwnTemplate) {
      items.push(
        <Dropdown.Item
          key="copy"
          disabled={isBusy}
          onClick={handleCopyTemplate}
        >
          {isCopying ? "Copying..." : "Copy to my templates"}
        </Dropdown.Item>,
      );
    }

    // Share / Unshare — admin only, own templates only
    if (isAdmin && isOwnTemplate) {
      if (isSharedTemplate) {
        items.push(
          <Dropdown.Item
            key="unshare"
            disabled={isBusy}
            onClick={handleUnshareTemplate}
          >
            {isSharing ? "Updating..." : "Unshare"}
          </Dropdown.Item>,
        );
      } else {
        items.push(
          <Dropdown.Item
            key="share"
            disabled={isBusy}
            onClick={handleShareTemplate}
          >
            {isSharing ? "Sharing..." : "Share template"}
          </Dropdown.Item>,
        );
      }
    }

    // Delete — creator only (BK-3285: no admin override)
    if (isOwnTemplate) {
      items.push(
        <Dropdown.Item
          key="delete"
          disabled={isBusy}
          onClick={handleTemplateDelete}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </Dropdown.Item>,
      );
    }

    return items;
  };

  return (
    <>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: react-select has no native input ID */}
      <label className="form-control-label">
        Template
        {infoTooltip && (
          <Tooltipable text={infoTooltip} trigger="click" className="ml5">
            <FontAwesomeIcon
              icon={["fal", "fa-info-circle"]}
              className="text-secondary"
              tabIndex={0}
              aria-label="Template information"
              style={{ fontSize: "0.75em", cursor: "pointer" }}
            />
          </Tooltipable>
        )}
      </label>
      <div className="d-flex">
        <div className="flex-grow-1">
          <Select
            placeholder="Select a Template"
            options={buildTemplateOptions()}
            value={selectedTemplate && selectedTemplate.value}
            onChange={handleTemplateSelect}
            isLoading={isLoading}
            isSearchable
            isClearable
            isDisabled={isBusy}
            filterOption={templateFilterOption}
          />
        </div>

        {selectedTemplate && (
          <div className="form-group mb5 ml5">
            <Dropdown show={dropdownOpen} onToggle={toggleDropdown}>
              <Dropdown.Toggle className="m0" variant="secondary">
                Options
              </Dropdown.Toggle>

              <Dropdown.Menu>{renderDropdownItems()}</Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
    </>
  );
};

TemplateSelect.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.shape({})),
  selectedTemplate: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setSelectedTemplate: PropTypes.func.isRequired,
  templateChanged: PropTypes.bool,
  setTemplateChanged: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isCreating: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isCopying: PropTypes.bool,
  isSharing: PropTypes.bool,
  updateTemplate: PropTypes.func,
  deleteTemplate: PropTypes.func,
  copyTemplate: PropTypes.func,
  shareTemplate: PropTypes.func,
  unshareTemplate: PropTypes.func,
  trackApply: PropTypes.func,
  applyTemplate: PropTypes.func,
  onCreate: PropTypes.func,
  isAdmin: PropTypes.bool,
  currentUserId: PropTypes.number,
  infoTooltip: PropTypes.string,
};

export default TemplateSelect;
